//deps
import React, { ErrorInfo, useCallback, useEffect, useMemo, useState } from "react";

// internal deps
import { CACHE_POSTFIX } from "@swiggy-private/aatm-nirbhar-build/dist/constants/htmlCaching";
import { ErrorPage } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/ErrorPage";
import GlobalStyled from "@swiggy-private/aatm-nirbhar-ui/dist/lib/GlobalStyled";
import { NewRelicErrorBoundary } from "@swiggy-private/aatm-nirbhar-ui/dist/lib/NewRelic";
import { AnalyticsContext } from "@swiggy-private/analytics";
import { BaseTheme } from "@swiggy-private/dls-web-common/dist/components/themes/Base";
import { FontStyle } from "@swiggy-private/dls-web-theme/dist/components/Fonts";
import { ThemeProvider } from "@swiggy-private/dls-web-theme/dist/components/ThemeProvider";
import {
    CartDataContextProvider,
    SwiggyHeadersContext,
    GenericErrorContext,
    PublicApiContext,
} from "@swiggy-private/portal-swiggy-one-subscription-context/dist";
import type { SwiggyHeaders } from "@swiggy-private/portal-swiggy-one-subscription-context/dist";

// eslint-disable-next-line import/order
import type { AppProps } from "next/app";

import { setLastScreen } from "analytics";
import { getAnalyticsContextValue } from "analytics/init";
import { useBootstrap } from "hooks/useBootstrap";
import { usePublicApiConfig } from "hooks/usePublicApiConfig";

// Analytics

// hooks

// styles
import { GlobalStyle } from "styles/globals";
import { ShuttleAppName } from "styles/home";

// utils
import { normalCloseWebView, isBrowserEnv } from "utils/bridgeSdk";
import { getSwiggyHeaders } from "utils/headers";
import { sendErrorBoundaryEvent } from "utils/newRelic";
import { NEW_RELIC_APPLICATION_ID } from "../constants";

const CLOSE = "click here to close";

function MyApp({ Component, pageProps, router }: AppProps) {
    // Detect if the page is a cached version
    const isCached = router.route.includes(CACHE_POSTFIX);
    useBootstrap("portal_swiggy_one_subscription", isCached);
    const [swiggyHeaders, setSwiggyHeaders] = useState<SwiggyHeaders>();
    const [genericError, setGenericError] = useState(false);
    const { enablePublicApi } = usePublicApiConfig();

    const onError = useCallback(
        (error?: Error, errorInfo?: ErrorInfo) => {
            sendErrorBoundaryEvent(error, errorInfo, swiggyHeaders);
        },
        [swiggyHeaders],
    );

    useEffect(() => {
        async function initApp() {
            if (isBrowserEnv()) {
                try {
                    const headers = await getSwiggyHeaders();
                    setLastScreen(headers?.referrer);
                    setSwiggyHeaders(headers);
                } catch (error) {
                    normalCloseWebView();
                }
            }
        }
        initApp();
    }, []);

    const analyticsContextValue = useMemo(() => getAnalyticsContextValue(swiggyHeaders), [swiggyHeaders]);

    const ErrorComponentWrapper = () => <ErrorPage onCtaClick={normalCloseWebView} ctaText={CLOSE} />;

    return (
        <NewRelicErrorBoundary
            applicationID={NEW_RELIC_APPLICATION_ID}
            onErrorCapture={onError}
            ErrorComponent={ErrorComponentWrapper}
        >
            <ThemeProvider theme={BaseTheme} colorMode="light">
                <ShuttleAppName data-app-name={`${process.env.NEXT_PUBLIC_APP_NAME}`} />
                <GlobalStyled />
                <GlobalStyle />
                <FontStyle />
                <SwiggyHeadersContext.Provider value={{ swiggyHeaders, setSwiggyHeaders }}>
                    <GenericErrorContext.Provider value={{ genericError, setGenericError }}>
                        <AnalyticsContext.Provider value={analyticsContextValue}>
                            <PublicApiContext.Provider value={{ enablePublicApi }}>
                                <CartDataContextProvider>
                                    <Component {...pageProps} headers={swiggyHeaders} />
                                </CartDataContextProvider>
                            </PublicApiContext.Provider>
                        </AnalyticsContext.Provider>
                    </GenericErrorContext.Provider>
                </SwiggyHeadersContext.Provider>
            </ThemeProvider>
        </NewRelicErrorBoundary>
    );
}

export default MyApp;
